// Interface for getWebcamProps return type
export interface WebcamPropsType {
  audio: boolean;
  screenshotFormat: 'image/png';
  screenshotQuality: number;
  videoConstraints: MediaTrackConstraints;
  style: {
    width: string;
    height: string;
    objectFit: 'cover' | 'contain';
  };
}

// Video quality tiers (priority from highest to lowest)
const VIDEO_QUALITY_TIERS = [
  {
    name: '4K Ultra HD',
    width: { ideal: 3840, min: 3200 },
    height: { ideal: 2160, min: 1800 },
    tier: 1,
  },
  {
    name: '2K Quad HD',
    width: { ideal: 2560, min: 2000 },
    height: { ideal: 1440, min: 1200 },
    tier: 2,
  },
  {
    name: 'Full HD',
    width: { ideal: 1920, min: 1600 },
    height: { ideal: 1080, min: 900 },
    tier: 3,
  },
  {
    name: 'HD Ready',
    width: { ideal: 1280, min: 1000 },
    height: { ideal: 720, min: 600 },
    tier: 4,
  },
  {
    name: 'Standard',
    width: { ideal: 854, min: 640 },
    height: { ideal: 480, min: 360 },
    tier: 5,
  },
];

// Test camera capability for a specific resolution
const testCameraCapability = async (
  constraints: MediaTrackConstraints,
): Promise<boolean> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: constraints,
      audio: false,
    });

    // Get actual video track settings
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) {
      stream.getTracks().forEach((track) => track.stop());
      return false;
    }
    const settings = videoTrack.getSettings();

    // Stop the stream immediately
    stream.getTracks().forEach((track) => track.stop());

    // Check if we got close to what we requested
    const requestedWidth =
      typeof constraints.width === 'object'
        ? constraints.width.ideal
        : constraints.width;
    const requestedHeight =
      typeof constraints.height === 'object'
        ? constraints.height.ideal
        : constraints.height;

    if (
      requestedWidth &&
      requestedHeight &&
      settings.width &&
      settings.height
    ) {
      const widthMatch = settings.width >= requestedWidth * 0.8; // Allow 20% tolerance
      const heightMatch = settings.height >= requestedHeight * 0.8;
      return widthMatch && heightMatch;
    }

    return true; // If we can't determine, assume it works
  } catch (error) {
    console.log(
      '🔧 Camera capability test failed:',
      error instanceof Error ? error.message : String(error),
    );
    return false;
  }
};

// Enhanced mobile video constraints for maximum quality
export const getMobileHighQualityConstraints = (
  aspectRatio: number = 16 / 9,
): MediaTrackConstraints => {
  return {
    width: { ideal: 4096, min: 1920 }, // Try for 4K, fallback to 1080p
    height: { ideal: 2160, min: 1080 },
    facingMode: 'environment',
    aspectRatio,
    frameRate: { ideal: 30, min: 15 },
  };
};

// Test mobile camera capability with enhanced settings
const testMobileHighQuality = async (
  aspectRatio: number,
): Promise<MediaTrackConstraints> => {
  try {
    const enhancedConstraints = getMobileHighQualityConstraints(aspectRatio);
    const stream = await navigator.mediaDevices.getUserMedia({
      video: enhancedConstraints,
      audio: false,
    });

    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) {
      const settings = videoTrack.getSettings();
      console.log('🔧 Mobile high quality settings achieved:', {
        width: settings.width,
        height: settings.height,
        aspectRatio: settings.aspectRatio,
        frameRate: settings.frameRate,
        facingMode: settings.facingMode,
      });

      // Stop the test stream
      stream.getTracks().forEach((track) => track.stop());
      return enhancedConstraints;
    }
  } catch (error) {
    console.log(
      '🔧 Mobile high quality failed, falling back to standard detection',
    );
  }

  // Fallback to standard detection
  return await detectBestVideoQuality(aspectRatio);
};

// Detect the best supported video quality
export const detectBestVideoQuality = async (
  aspectRatio = 16 / 9,
  forceMobileHighQuality = false,
): Promise<MediaTrackConstraints> => {
  // For mobile devices, try enhanced quality first
  const isMobileDevice =
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

  if (isMobileDevice && forceMobileHighQuality) {
    return await testMobileHighQuality(aspectRatio);
  }

  for (const tier of VIDEO_QUALITY_TIERS) {
    const constraints: MediaTrackConstraints = {
      width: tier.width,
      height: tier.height,
      facingMode: 'environment',
      aspectRatio,
      frameRate: { ideal: 30, min: 15 },
    };

    const isSupported = await testCameraCapability(constraints);

    if (isSupported) {
      return constraints;
    }
  }

  return {
    facingMode: 'environment',
    aspectRatio,
    frameRate: { ideal: 30, min: 15 },
  };
};

export const getVideoConstraints = async (
  aspectRatio = 16 / 9,
  highQuality = true,
): Promise<MediaTrackConstraints> => {
  // Detect and return the best supported quality
  return await detectBestVideoQuality(aspectRatio, highQuality);
};

export const getWebcamProps = async (aspectRatio = 16 / 9) => {
  // Always force high quality
  const videoConstraints = await getVideoConstraints(aspectRatio, true);

  return {
    audio: false,
    screenshotFormat: 'image/png' as const,
    screenshotQuality: 1,
    videoConstraints,
    style: {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
    },
  };
};

export const calculateCameraDimensions = (
  aspectRatio: number,
  isMobile: boolean,
) => {
  if (isMobile) {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const maxGuidingFrameWidth = viewportWidth * 0.6;
    const calculatedHeight = maxGuidingFrameWidth * aspectRatio;

    const availableHeight = viewportHeight * 0.7;

    let guidingFrameWidth: number, guidingFrameHeight: number;

    if (calculatedHeight <= availableHeight) {
      guidingFrameWidth = maxGuidingFrameWidth;
      guidingFrameHeight = calculatedHeight;
    } else {
      guidingFrameHeight = availableHeight;
      guidingFrameWidth = availableHeight / aspectRatio;
    }

    return {
      containerWidth: viewportWidth,
      containerHeight: viewportHeight,
      guidingFrameHeight,
      guidingFrameWidth,
    };
  } else {
    const containerHeight = 400;
    const containerWidth = containerHeight * aspectRatio;
    const guidingFrameHeight = containerHeight * 0.8;
    const guidingFrameWidth = guidingFrameHeight * aspectRatio;
    return {
      containerWidth,
      containerHeight,
      guidingFrameHeight,
      guidingFrameWidth,
    };
  }
};

export const cropImageToGuidingFrame = async (
  imageSrc: string,
  aspectRatio: number,
  isMobile: boolean,
): Promise<string> => {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const {
        containerWidth,
        containerHeight,
        guidingFrameWidth,
        guidingFrameHeight,
      } = calculateCameraDimensions(aspectRatio, isMobile);

      const guidingFrameX = (containerWidth - guidingFrameWidth) / 2;
      const guidingFrameY = (containerHeight - guidingFrameHeight) / 2;

      const imageAspectRatio = img.width / img.height;
      const containerAspectRatio = containerWidth / containerHeight;

      let videoDisplayWidth: number;
      let videoDisplayHeight: number;
      let videoOffsetX = 0;
      let videoOffsetY = 0;

      if (imageAspectRatio > containerAspectRatio) {
        videoDisplayHeight = containerHeight;
        videoDisplayWidth = containerHeight * imageAspectRatio;
        videoOffsetX = (videoDisplayWidth - containerWidth) / 2;
      } else {
        videoDisplayWidth = containerWidth;
        videoDisplayHeight = containerWidth / imageAspectRatio;
        videoOffsetY = (videoDisplayHeight - containerHeight) / 2;
      }

      const extra = 20;

      const cropX =
        (guidingFrameX + videoOffsetX) * (img.width / videoDisplayWidth) -
        extra;
      const cropY =
        (guidingFrameY + videoOffsetY) * (img.height / videoDisplayHeight) -
        extra;
      const cropWidth =
        guidingFrameWidth * (img.width / videoDisplayWidth) + extra * 2;
      const cropHeight =
        guidingFrameHeight * (img.height / videoDisplayHeight) + extra * 2;

      const clampedCropX = Math.max(0, Math.min(cropX, img.width - cropWidth));
      const clampedCropY = Math.max(
        0,
        Math.min(cropY, img.height - cropHeight),
      );
      const clampedCropWidth = Math.min(cropWidth, img.width - clampedCropX);
      const clampedCropHeight = Math.min(cropHeight, img.height - clampedCropY);

      const cropCanvas = document.createElement('canvas');
      cropCanvas.width = clampedCropHeight;
      cropCanvas.height = clampedCropWidth;

      const ctx = cropCanvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      ctx.save();
      ctx.translate(0, clampedCropWidth);
      ctx.rotate(-Math.PI / 2);
      ctx.drawImage(
        img,
        clampedCropX,
        clampedCropY,
        clampedCropWidth,
        clampedCropHeight,
        0,
        0,
        clampedCropWidth,
        clampedCropHeight,
      );
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.restore();

      const src = window.cv.imread(cropCanvas);
      const dst = new window.cv.Mat();

      const meanScalar = window.cv.mean(src);
      const averageBrightness = meanScalar[0];

      const kernel = window.cv.Mat.ones(3, 3, window.cv.CV_32F);
      if (averageBrightness > 80) {
        kernel.data32F.set([0, -0.6, 0, -0.6, 3.2, -0.6, 0, -0.6, 0]);
        window.cv.filter2D(src, dst, window.cv.CV_8U, kernel);
        window.cv.convertScaleAbs(dst, dst, 1.5, 0);
      } else {
        kernel.data32F.set([0, -0.5, 0, -0.5, 3, -0.5, 0, -0.5, 0]);
        window.cv.filter2D(src, dst, window.cv.CV_8U, kernel);
        window.cv.convertScaleAbs(dst, dst, 1.2, 10);
      }

      window.cv.resize(
        dst,
        dst,
        new window.cv.Size(cropCanvas.width * 4, cropCanvas.height * 4),
        0,
        0,
        window.cv.INTER_LANCZOS4,
      );

      window.cv.imshow(cropCanvas, dst);

      src.delete();
      dst.delete();
      kernel.delete();

      const enhancedImage = cropCanvas.toDataURL('image/png');
      resolve(enhancedImage);
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageSrc;
  });
};

export const calculateGuidingFrameForCanvas = (
  videoWidth: number,
  videoHeight: number,
  frameWidthRatio = 0.3,
  frameHeightRatio = 0.5,
) => {
  const guidingFrameWidth = videoWidth * frameWidthRatio;
  const guidingFrameHeight = videoHeight * frameHeightRatio;

  return {
    x: (videoWidth - guidingFrameWidth) / 2,
    y: (videoHeight - guidingFrameHeight) / 2,
    width: guidingFrameWidth,
    height: guidingFrameHeight,
  };
};
